import clsx from "clsx";
import {
  ChangeEvent,
  ReactNode,
  forwardRef,
  useCallback,
  useState,
} from "react";
import { ControlStaticType, FormControlChildProps } from "../form-control";

export interface CheckboxProps extends FormControlChildProps {
  label?: ReactNode;

  value?: string;

  /**
   * This property allow client to set initial checked state.
   *
   * Default: false
   */
  defaultChecked?: boolean;

  className?: string;

  labelClassName?: string;

  checkboxClassName?: string;

  errorClassName?: string;

  isMultiLanguage?: boolean;

  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
}

const CheckboxComponent = (props: CheckboxProps, ref: any) => {
  const {
    id,
    formik,
    label,
    name,
    value,
    checked,
    defaultChecked = false,
    disabled = false,
    className = "",
    labelClassName = "",
    checkboxClassName = "",
    errorClassName = "",
    status,
    onChange,
    onBlur,
    fmOnChange,
    fmOnBlur,
  } = props;
  const [innerChecked, setCheckedState] = useState(defaultChecked);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e);

      fmOnChange && fmOnChange(e);

      setCheckedState(e.target.checked);
    },
    [onChange, fmOnChange]
  );

  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onBlur && onBlur(e);

      fmOnBlur && fmOnBlur(e);
    },
    [onBlur, fmOnBlur]
  );

  const isChecked = checked !== undefined ? checked : innerChecked;

  return (
    <div className="flex-col justify-center">
      <label
        className={clsx(
          `flex items-center relative rounded-full cursor-pointer ${className}`
        )}
      >
        <input
          ref={ref}
          id={id}
          value={value}
          name={name}
          className={clsx(
            `mr-2 ${checkboxClassName}`,
            "before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-[#CFD3D4]",
            "transition-all before:absolute before:top-2/4 before:left-2/4 before:-translate-y-2/4 before:-translate-x-2/4",
            "before:rounded-full before:bg-blue-gray-500  before:transition-opacity before:block before:h-6 before:w-6",
            "checked:border-[#5570F1] checked:bg-[#5570F1] checked:before:border checked:before:border-solid",
            "checked:before:border-[#5570F1] checked:before:rounded-lg"
          )}
          type="checkbox"
          disabled={disabled}
          checked={isChecked}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-3.5 -translate-y-2/4 -translate-x-[80%] peer-checked:opacity-100">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className={clsx(`mt-[2px] ${labelClassName}`)}>{label}</div>
      </label>
      {!!status && (
        <div className={`flex error-color text-xs ${errorClassName}`}>
          {formik &&
            formik.getFieldMeta(name!).error &&
            formik.getFieldMeta(name!).error}
        </div>
      )}
    </div>
  );
};

CheckboxComponent.staticType = ControlStaticType.CHECKBOX;

export default forwardRef(CheckboxComponent);
